import React, { Fragment, useEffect, useRef, useState } from "react";
import * as styles from "./ExtendedPropertyList.module.scss";
import classNames from "classnames";
import { Col, Container, Row } from "react-bootstrap";
import Button from "@app/components/Button";
import Panel from "@app/components/Panel";
import ModalOverlay from "@app/components/Modal";
import FormBuilder from "@app/components/FormBuilder";
import FormBuilderElements from "@app/components/FormBuilder/FormBuilderElements";
import { filterFollowUp, getInitialValues } from "@app/components/FormBuilder/FormBuilder";
import { FieldArray, useField, useFormikContext } from "formik";
import MainObserver from "@app/components/ExtendedPropertyList/MainObserver.jsx";
import { useDangerFieldsObserver } from "@app/components/ExtendedPropertyList/DangerFieldsObserver.jsx";
import getDisplayFields from "@app/lib/hooks/getDisplayFields";
import { validateFields } from "@app/utils/inspection";
import Hint from "@app/components/Hint";
import { PropertyList } from "@app/components/ExtendedPropertyList/PropertyList";
import { DEPLOYMENT_STATE_KEYS, validateStep } from "@app/utils/confirmationState";
import { RemainingHazardsMessage } from "@app/components/inspection-form/DangerListObserver.jsx";

function getSubHeaderText(values, listCategories) {
	if (!listCategories) return null;
	const category = listCategories.find((list) => {
		return list.selectorValue === values[list.selectorKey];
	});
	if (category) {
		const displayValue = values[category.displayKey];
		return displayValue?.length > 0 ? `${displayValue} | ${category.listLabel}` : category.listLabel;
	}

	return null;
}

function getErrorCountPerEntry(value, validation, TEMP_ELEM_KEY = "currentElement") {
	const validationResult = validateStep(
		{
			[TEMP_ELEM_KEY]: value,
		},
		[
			{
				elements: [
					{
						name: TEMP_ELEM_KEY,
						validation,
					},
				],
			},
		],
	);

	const validationPerEntry = value.map((entry, i) => {
		return DEPLOYMENT_STATE_KEYS.reduce(
			(acc, KEY) => ({
				...acc,
				[KEY]: Object.entries(validationResult[KEY]).filter(([key]) => key.startsWith(`${TEMP_ELEM_KEY}[${i}]`))
					.length,
			}),
			{},
		);
	});

	return validationPerEntry;
}

function getErrors(value, validation, TEMP_ELEM_KEY = "currentElement") {
	const validationResult = validateStep(
		{
			[TEMP_ELEM_KEY]: value,
		},
		[
			{
				elements: [
					{
						name: TEMP_ELEM_KEY,
						validation,
					},
				],
			},
		],
	);
	return validationResult;
}

const ExtendedPropertyList = ({
	type = "panel",
	overlayType = "overlay",
	name,
	title,
	button = "Hinzufügen",
	fieldsets = [],
	formPage,
	isFollowUp,
	observerProps,
	listCategories,
	validation,
	instantErrors,
	...specialProps
}) => {
	const ExtendedPropertyListClassName = classNames({
		[styles["extended-property-list"]]: true,
	});
	const [{ value } /*meta*/] = useField({ name });
	({ fieldsets } = useDangerFieldsObserver(fieldsets));
	fieldsets.map((fieldset) => {
		fieldset.elements = fieldset.elements || [];
	});
	const fieldsetElements = fieldsets.flatMap(({ elements }) => elements);
	const resetValues = getInitialValues(fieldsetElements);
	const panelElements = fieldsetElements
		?.filter((element) => filterFollowUp(isFollowUp, element))
		.filter(({ displayPrimary }) => !!displayPrimary);

	const [showModal, setShowModal] = useState(false);
	const [editItem, setEditItem] = useState(null);
	const [subheader, setSubheader] = useState(false);
	const [modalErrors, setModalErrors] = useState([]);
	const { values: globalValues } = useFormikContext();
	const errorHintRef = useRef(null);
	const hasMinCountError = instantErrors?.some(e => e.path === name && e.type === "minCount") ?? false;

	useEffect(() => {
		if (modalErrors.length > 0) {
			errorHintRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [modalErrors]);

	const mapWithName = (array, index) => {
		return array.map((elem) => ({
			...elem,
			name: `${name}.${index}.${elem.name}`,
		}));
	};

	const mapWithNameObj = (object, index) => {
		let newObject = {};

		Object.entries(object).forEach(([key, val]) => {
			newObject[`${name}.${index}.${key}`] = val;
		});

		return newObject;
	};

	const getPropertyData = (currentValue, displayKey) => {
		let headerElement, header;
		headerElement = fieldsetElements.filter(({ showAsHeader }) => !!showAsHeader);

		if (displayKey === "physischegefaehrdungsanalyse_gefaehrdungstyp_physische_gefaehrdung") {
		}
		header = headerElement
			?.filter(({ name }) => {
				return currentValue[name]?.length > 0;
			})
			.map(({ name }, index) => <span key={index}>{currentValue[name]}</span>);

		if (
			displayKey === "physischegefaehrdungsanalyse_gefaehrdungstyp_physische_gefaehrdung" &&
			currentValue[displayKey] === "Sonstige Gefährdung"
		) {
			// physischegefaehrdungsanalyse_gefaehrdungstyp_physische_gefaehrdung_sonstige
			header = fieldsetElements
				?.filter(
					({ name }) =>
						name === "physischegefaehrdungsanalyse_gefaehrdungstyp_physische_gefaehrdung_sonstige",
				)
				.map(({ name }, index) => <span key={index}>{currentValue[name]}</span>);
		}

		let headerFormGroup;
		if (!header && headerElement && "formGroup" in headerElement) {
			headerFormGroup = currentValue[headerElement?.formGroup?.elements[0]?.name]?.join(", ");
		}

		const tooltip = currentValue[fieldsetElements.find(({ showAsTooltip }) => !!showAsTooltip)?.name];

		return {
			header,
			headerFormGroup,
			tooltip,
		};
	};

	const PropertyPanel = ({ value, remove }) => {
		return value.map((currentValue, index) => {
			const { header, headerFormGroup, tooltip } = getPropertyData(currentValue);

			return (
				<Panel
					key={index}
					header={{
						label: header || headerFormGroup,
						tooltip: tooltip ?? undefined,
						noSeparator: true,
						actions: [
							{
								type: "quaternary",
								iconBefore: "edit",
								callback: () => {
									setEditItem(currentValue);
									setShowModal(true);
								},
							},
							{
								type: "tertiary",
								iconBefore: "bin",
								callback: () => {
									remove(index);
								},
							},
						],
					}}
				>
					<FormBuilderElements
						values={{
							...getInitialValues(mapWithName(panelElements, index)),
							...mapWithNameObj(currentValue, index),
						}}
						displayFields={mapWithName(panelElements, index)}
					/>
				</Panel>
			);
		});
	};

	return (
		<>
			<Container className={ExtendedPropertyListClassName}>
				<Row>
					<FieldArray name={name}>
						{(params) => {
							const { push, replace, remove } = params;
							return (
								<Col xs={12}>
									{value?.length > 0 ? (
										type === "list" ? (
											listCategories ? (
												listCategories.map((cat) => {
													const filteredValues = value.filter(
														(entry) => entry[cat.selectorKey] === cat.selectorValue,
													);
													const errorCountPerEntry = getErrorCountPerEntry(
														filteredValues,
														validation,
														name,
													);
													let errors = getErrors(filteredValues, validation, name) || {};
													const mappedErrors = DEPLOYMENT_STATE_KEYS.reverse().reduce(
														(acc, key) => ({
															...acc,
															...errors[key],
														}),
														{},
													);
													return filteredValues?.length > 0 ? (
														<>
															{cat.listLabel && (
																<p className={"mb-6 mt-18"}>{cat.listLabel}</p>
															)}
															<PropertyList
																value={filteredValues}
																remove={(currentValue) => {
																	const index = value.indexOf(currentValue);
																	remove(index);
																}}
																displayKey={cat.displayKey}
																fieldsetElements={fieldsetElements}
																isFollowUp={isFollowUp}
																overlayType={overlayType}
																getPropertyData={getPropertyData}
																mapWithName={mapWithName}
																mapWithNameObj={mapWithNameObj}
																setEditItem={setEditItem}
																setShowModal={setShowModal}
																extendedSummary={
																	specialProps.extendedSummary
																		? {
																				name,
																				fieldsets,
																				errorCountPerEntry,
																			}
																		: null
																}
																errors={mappedErrors}
															/>
														</>
													) : null;
												})
											) : (
												<PropertyList
													value={value}
													remove={remove}
													fieldsetElements={fieldsetElements}
													isFollowUp={isFollowUp}
													overlayType={overlayType}
													getPropertyData={getPropertyData}
													mapWithName={mapWithName}
													mapWithNameObj={mapWithNameObj}
													setEditItem={setEditItem}
													setShowModal={setShowModal}
												/>
											)
										) : (
											<PropertyPanel value={value} remove={remove} />
										)
									) : (
										<div className={"mt-18 mb-18 fw-bold text-dim"}>
											{specialProps.emptyMessage}
										</div>
									)}

									<Row className={"justify-content-center"}>
										<Col xs={12} sm={12} className={"text-center"}>
											<Button
												label={button}
												type={"quaternary"}
												big={true}
												iconBefore={"add"}
												iconSize={12}
												className={`mt-18 ${styles["add-button"]}`}
												callback={() => {
													setShowModal(!showModal);
												}}
											/>
										</Col>
									</Row>
									<RemainingHazardsMessage name={name} hasMinCountError={hasMinCountError} />

									<ModalOverlay
										header={title}
										subheader={subheader}
										show={showModal}
										fullscreen={overlayType === "page"}
										onHide={() => {
											setShowModal(false);
											setSubheader(false);
											setEditItem(null);
										}}
									>
										{modalErrors?.length > 0 && (
											<div ref={errorHintRef} className="pb-30">
												<Hint
													label={"Bitte füllen Sie zum Fortfahren die markierten Felder aus."}
												/>
											</div>
										)}
										<FormBuilder
											values={editItem ? editItem : getInitialValues(fieldsetElements)}
											onChange={({ formikProps, formikFields }) => {
												const displayFields = getDisplayFields(
													formikFields,
													formikProps.values,
												);
												const validationErrors = validateFields(
													displayFields,
													formikProps.values,
													true,
												);
												const errors = validationErrors.reduce((res, val) => {
													return {
														...res,
														[val.path]: val.message,
													};
												}, {});
												formikProps.setErrors(errors); // create error messages

												if (
													true ||
													formikProps.values?.physischegefaehrdungsanalyse_gefaehrdungstyp_typ
														?.length > 0
												) {
													setSubheader({
														// icon: option?.icon ?? "",
														text: getSubHeaderText(formikProps.values, listCategories),
													});
												}
											}}
											isOverlay={overlayType !== "page"}
											isSubPage={overlayType === "page"}
											header={formPage?.header}
											subheader={formPage?.subheader}
											fieldsets={fieldsets}
											instantErrors={instantErrors}
											Navigation={({ formikProps, formikFields }) => {
												return (
													<div className={"modal-buttons"}>
														<Button
															label={"Abbrechen"}
															type={"secondary"}
															callback={() => {
																setShowModal(false);
																setEditItem(null);
																setSubheader(false);
															}}
														/>
														<Button
															label={editItem ? "Speichern" : "Hinzufügen"}
															callback={() => {
																const displayFields = getDisplayFields(
																	formikFields,
																	formikProps.values,
																);
																const modalValidationErrors = validateFields(
																	displayFields,
																	formikProps.values,
																	true,
																);
																setModalErrors(modalValidationErrors);
																if (modalValidationErrors?.length <= 0) {
																	setShowModal(false);
																	setSubheader(false);
																	if (editItem) {
																		const index = value.findIndex(
																			(entry) => entry === editItem,
																		);
																		replace(index, formikProps.values);
																		setEditItem(null);
																	} else {
																		push(formikProps.values);
																	}
																} else {
																	const errors = modalValidationErrors.reduce((res, val) => {
																			return {
																				...res,
																				[val.path]: val.message,
																			};
																		},
																		{},
																	);
																	formikProps.setErrors(errors);
																}
															}}
															isSubmit={true}
															// disabled={}
														/>
													</div>
												);
											}}
											observers={[MainObserver]}
											observerProps={{ globalValues, ...observerProps, resetValues }}
											proceedInvalid={false}
										/>
									</ModalOverlay>
								</Col>
							);
						}}
					</FieldArray>
				</Row>
				{/*{meta.error && (
					<Row>
						<Col>{meta.error}</Col>
					</Row>
				)}*/}
			</Container>
		</>
	);
};

ExtendedPropertyList.propTypes = {};

export default ExtendedPropertyList;
